/**
 * Natural Voice Service
 * Uses OpenAI TTS when available, falls back to Web Speech API
 * Provides consistent API for all voice output in the app
 */

export type Language = 'en' | 'es';

interface SpeakOptions {
  text: string;
  language: Language;
  onComplete?: () => void;
  /** HTMLAudio playback rate (OpenAI path only). Default 1. Use >1 for snappier letter clips. */
  playbackRate?: number;
}

// Once API fails, skip it for the session to avoid repeated failed fetches
let apiDisabled = false;
let currentAudio: HTMLAudioElement | null = null;

/**
 * Monotonic request token. Bumped every time `stop()` is called, so any
 * in-flight `speak()` whose fetch resolves after a newer call can detect
 * it has been superseded and skip playback. Without this, two near-
 * simultaneous speak() invocations (e.g. React StrictMode's double-mount
 * in dev, or a fast user click while the welcome line is still fetching)
 * each await their own /api/text-to-speech response and then both play
 * audio at once — perceived as two voices talking over each other.
 */
let speakRequestId = 0;

// ── Speech subscription (for the global SpeakingSubtitle UI) ────────────
// Any UI that wants to show "the AI is currently speaking" + a live
// subtitle subscribes here. We emit on every state transition so the
// indicator reflects the *current* speech regardless of which screen
// triggered it.
export type SpeechState = 'loading' | 'speaking' | 'idle';
export interface SpeechEvent {
  state: SpeechState;
  text: string | null;
  language: Language | null;
}
const speechSubscribers = new Set<(e: SpeechEvent) => void>();
let lastSpeechEvent: SpeechEvent = { state: 'idle', text: null, language: null };

export function subscribeToSpeech(cb: (e: SpeechEvent) => void): () => void {
  speechSubscribers.add(cb);
  // Replay the last event so a freshly-mounted subscriber knows current state.
  cb(lastSpeechEvent);
  return () => {
    speechSubscribers.delete(cb);
  };
}

function emitSpeech(event: SpeechEvent): void {
  lastSpeechEvent = event;
  speechSubscribers.forEach((cb) => {
    try {
      cb(event);
    } catch {
      /* subscriber blew up — don't take down the audio pipeline */
    }
  });
}

let audioUnlocked = false;

/** Call once on app load to unlock audio on first user interaction */
export function initAudioUnlock(): void {
  if (audioUnlocked) return;
  const unlock = () => {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioContextClass) {
      const ctx = new AudioContextClass();
      if (ctx.state === 'suspended') ctx.resume().then(() => { audioUnlocked = true; });
      else audioUnlocked = true;
    }
  };
  ['click', 'touchstart', 'keydown'].forEach((ev) => {
    window.addEventListener(ev, unlock, { once: true, passive: true });
  });
}

/** Play a magic sparkle sound for instant feedback while TTS loads */
export function playClickFeedback(): void {
  try {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    if (ctx.state === 'suspended') void ctx.resume();
    const now = ctx.currentTime;
    [523.25, 659.25, 783.99].forEach((f, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(f, now);
      const t0 = now + i * 0.06;
      gain.gain.setValueAtTime(0, t0);
      gain.gain.linearRampToValueAtTime(0.45, t0 + 0.04);
      gain.gain.exponentialRampToValueAtTime(0.01, t0 + 0.22);
      osc.start(t0);
      osc.stop(t0 + 0.23);
    });
  } catch {
    /* AudioContext not available */
  }
}

/** Slightly faster playback for letter/animal clips (OpenAI TTS only). */
const TTS_PLAYBACK_RATE = 1.08;

// Cache TTS blobs to reduce API calls (avoids 429 on repeated phrases)
const CACHE_MAX = 120;
const cache = new Map<string, Blob>();
function cacheKey(text: string, language: Language): string {
  return `${language}|${text}`;
}

// TTS struggles with single letters; use phonetic spellings so each letter is audible
const LETTER_PHONETIC_EN: Record<string, string> = {
  A: 'Ay', B: 'Bee', C: 'See', D: 'Dee', E: 'Ee', F: 'Eff', G: 'Gee', H: 'Aitch',
  I: 'Eye', J: 'Jay', K: 'Kay', L: 'El', M: 'Em', N: 'En', O: 'Oh', P: 'Pee',
  Q: 'Cue', R: 'Ar', S: 'Ess', T: 'Tee', U: 'You', V: 'Vee', W: 'Double you',
  X: 'Ex', Y: 'Why', Z: 'Zee',
};
const LETTER_PHONETIC_ES: Record<string, string> = {
  A: 'Ah', B: 'Be', C: 'Se', D: 'De', E: 'E', F: 'Efe', G: 'Ge', H: 'Hache',
  I: 'I', J: 'Jota', K: 'Ka', L: 'Ele', M: 'Eme', N: 'Ene', O: 'O', P: 'Pe',
  Q: 'Cu', R: 'Ere', S: 'Ese', T: 'Te', U: 'U', V: 'Uve', W: 'Doble u',
  X: 'Equis', Y: 'I griega', Z: 'Zeta', Ñ: 'Eñe',
};

function textForTTS(text: string, language: Language): string {
  const trimmed = text.trim();
  if (trimmed.length === 1) {
    const upper = trimmed.toUpperCase();
    const map = language === 'en' ? LETTER_PHONETIC_EN : LETTER_PHONETIC_ES;
    if (map[upper]) return map[upper];
  }
  return text;
}

/** Letter name for speech (Ay, Bee, … / Ah, Be, …) */
export function letterPhoneticName(letter: string, language: Language): string {
  const upper = letter.trim().toUpperCase();
  const map = language === 'en' ? LETTER_PHONETIC_EN : LETTER_PHONETIC_ES;
  return map[upper] ?? letter.trim();
}

/** One TTS blob for letter + animal + fact — avoids 3 sequential API round-trips. */
export function letterAnimalNatureUtterance(
  letter: string,
  animal: string,
  natureFact: string,
  language: Language
): string {
  const sound = letterPhoneticName(letter, language);
  if (language === 'en') {
    return `${sound} is for ${animal}. ${natureFact}`;
  }
  return `${sound}. ${letter} de ${animal}. ${natureFact}`;
}

function letterAnimalUtterance(letter: string, animal: string, language: Language): string {
  const sound = letterPhoneticName(letter, language);
  if (language === 'en') {
    return `${sound} is for ${animal}.`;
  }
  return `${sound}. ${letter} de ${animal}.`;
}

const EN_LANG = 'en-US';
const ES_LANG = 'es-ES';
const FEMALE_HINTS = ['female', 'woman', 'samantha', 'victoria', 'karen', 'moira', 'kate', 'elena', 'paloma', 'paulina', 'google español', 'microsoft sabina'];

function pickVoice(language: Language): SpeechSynthesisVoice | null {
  const lang = language === 'en' ? EN_LANG : ES_LANG;
  const langPrefix = language === 'en' ? 'en' : 'es';
  const voices = speechSynthesis.getVoices();
  const matching = voices.filter((v) => v.lang.toLowerCase().startsWith(langPrefix));
  const female = matching.find((v) =>
    FEMALE_HINTS.some((h) => v.name.toLowerCase().includes(h))
  );
  const exact = matching.find((v) => v.lang === lang);
  return female || exact || matching[0] || null;
}

function speakWithWebSpeech(
  text: string,
  language: Language,
  onComplete?: () => void,
  /** Roughly match faster TTS when API unavailable (rate is relative, not same as HTMLAudio). */
  htmlAudioRateHint = 1
): void {
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = htmlAudioRateHint > 1 ? 1.08 : 1;
  utterance.pitch = 1.2;
  utterance.volume = 1;
  utterance.lang = language === 'en' ? EN_LANG : ES_LANG;

  const voice = pickVoice(language);
  if (voice) utterance.voice = voice;

  utterance.onstart = () => emitSpeech({ state: 'speaking', text, language });
  utterance.onend = () => {
    emitSpeech({ state: 'idle', text: null, language: null });
    onComplete?.();
  };
  utterance.onerror = () => {
    emitSpeech({ state: 'idle', text: null, language: null });
    onComplete?.();
  };

  speechSynthesis.cancel();
  speechSynthesis.speak(utterance);
}

const RETRY_DELAY_MS = 2000;
const MAX_RETRIES = 2;

async function fetchWithRetry(
  url: string,
  opts: RequestInit,
  retries = 0
): Promise<Response> {
  const res = await fetch(url, opts);
  if (res.status === 429 && retries < MAX_RETRIES) {
    const delay = RETRY_DELAY_MS * (retries + 1);
    await new Promise((r) => setTimeout(r, delay));
    return fetchWithRetry(url, opts, retries + 1);
  }
  return res;
}

/**
 * Speak text using natural voice (OpenAI TTS) when available,
 * otherwise falls back to Web Speech API
 */
export async function speak(options: SpeakOptions): Promise<void> {
  const { text, language, onComplete, playbackRate: rateOpt } = options;
  const playbackRate = rateOpt ?? 1;

  stop();
  // Claim this turn. If anyone else calls stop()/speak() while we're
  // awaiting fetch/play, speakRequestId will move past myId and we bail.
  const myId = speakRequestId;

  // Subtitle bar shows "loading" immediately so the user has visual
  // confirmation a request is in flight even before audio bytes arrive.
  emitSpeech({ state: 'loading', text, language });

  // Helper: only this turn is allowed to clear the subtitle bar.
  const finish = (cb?: () => void) => {
    if (myId === speakRequestId) {
      emitSpeech({ state: 'idle', text: null, language: null });
    }
    cb?.();
  };

  const ttsText = textForTTS(text, language);
  const key = cacheKey(ttsText, language);
  const cached = cache.get(key);
  if (cached) {
    if (myId !== speakRequestId) return;
    const url = URL.createObjectURL(cached);
    const audio = new Audio(url);
    audio.preload = 'auto';
    if (playbackRate !== 1) audio.playbackRate = playbackRate;
    currentAudio = audio;
    audio.onplay = () => {
      if (myId === speakRequestId) emitSpeech({ state: 'speaking', text, language });
    };
    audio.onended = () => {
      currentAudio = null;
      URL.revokeObjectURL(url);
      finish(onComplete);
    };
    audio.onerror = () => {
      currentAudio = null;
      URL.revokeObjectURL(url);
      finish(onComplete);
    };
    await audio.play();
    return;
  }

  if (!apiDisabled) {
    try {
      const res = await fetchWithRetry('/api/text-to-speech', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: ttsText, language }),
      });
      if (myId !== speakRequestId) return;

      if (!res.ok) {
        // Only disable API for auth/server errors, not rate limits
        if (res.status === 401 || res.status === 403 || res.status === 503) {
          apiDisabled = true;
        }
        throw new Error(`TTS API error: ${res.status}`);
      }

      const blob = await res.blob();
      if (myId !== speakRequestId) return;
      if (cache.size < CACHE_MAX) cache.set(key, blob);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.preload = 'auto';
      if (playbackRate !== 1) audio.playbackRate = playbackRate;
      currentAudio = audio;

      audio.onplay = () => {
        if (myId === speakRequestId) emitSpeech({ state: 'speaking', text, language });
      };
      audio.onended = () => {
        currentAudio = null;
        URL.revokeObjectURL(url);
        finish(onComplete);
      };
      audio.onerror = () => {
        currentAudio = null;
        URL.revokeObjectURL(url);
        finish(onComplete);
      };

      await audio.play();
      return;
    } catch (err) {
      if (myId !== speakRequestId) return;
      console.warn('Natural voice API failed, using Web Speech:', err);
    }
  }

  if (myId !== speakRequestId) return;
  // speakWithWebSpeech emits its own speaking/idle transitions via the
  // utterance.onstart / onend / onerror handlers above, so we don't
  // double-emit here. We do still need to forward onComplete cleanly.
  speakWithWebSpeech(ttsText, language, () => finish(onComplete), playbackRate);
}

/**
 * Speak a sequence of phrases with a pause between each (e.g. letter, pause, animal)
 */
export async function speakSequence(options: {
  phrases: string[];
  language: Language;
  pauseMs?: number;
  onComplete?: () => void;
}): Promise<void> {
  const { phrases, language, pauseMs = 500, onComplete } = options;
  if (phrases.length === 0) {
    onComplete?.();
    return;
  }
  stop();
  const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
  for (let i = 0; i < phrases.length; i++) {
    if (phrases[i]) {
      await new Promise<void>((resolve) => {
        speak({ text: phrases[i], language, onComplete: resolve });
      });
    }
    if (i < phrases.length - 1) await delay(pauseMs);
  }
  onComplete?.();
}

/**
 * Prefetch TTS into cache (no playback). Call when grid is revealed to make first clicks feel instant.
 */
export async function prefetch(text: string, language: Language): Promise<void> {
  const ttsText = textForTTS(text, language);
  const key = cacheKey(ttsText, language);
  if (cache.has(key)) return;
  if (apiDisabled) return;
  try {
    const res = await fetch('/api/text-to-speech', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: ttsText, language }),
    });
    if (res.ok) {
      const blob = await res.blob();
      if (cache.size < CACHE_MAX) cache.set(key, blob);
    }
  } catch {
    /* ignore prefetch errors */
  }
}

/**
 * Speak letter + animal in the standard fluid format (used by both grid and tracing)
 */
export function speakLetterAndAnimal(
  letter: string,
  animal: string,
  language: Language,
  _opts?: { pauseMs?: number }
): void {
  void speak({
    text: letterAnimalUtterance(letter, animal, language),
    language,
    playbackRate: TTS_PLAYBACK_RATE,
  });
}

/**
 * Letter + animal, then a nature / sustainability line (fun fact from data).
 * Single TTS utterance so playback starts after one fetch (was 3 sequential calls).
 */
export function speakLetterAnimalAndNature(
  letter: string,
  animal: string,
  natureFact: string,
  language: Language,
  _opts?: { pauseMs?: number }
): void {
  void speak({
    text: letterAnimalNatureUtterance(letter, animal, natureFact, language),
    language,
    playbackRate: TTS_PLAYBACK_RATE,
  });
}

/** Warm cache for grid / embed — pass same args as speakLetterAnimalAndNature. */
export function prefetchLetterAnimalAndNature(
  letter: string,
  animal: string,
  natureFact: string,
  language: Language
): Promise<void> {
  return prefetch(letterAnimalNatureUtterance(letter, animal, natureFact, language), language);
}

export type LetterAnimalNatureEntry = { letter: string; animal: string; natureFact: string };

/** Prefetch many combined clips in parallel (bounded concurrency) so taps hit cache quickly. */
export async function prefetchCombinedUtterances(
  entries: LetterAnimalNatureEntry[],
  language: Language,
  concurrency = 6
): Promise<void> {
  if (entries.length === 0 || apiDisabled) return;
  let next = 0;
  const worker = async () => {
    for (;;) {
      const i = next++;
      if (i >= entries.length) return;
      const e = entries[i];
      await prefetchLetterAnimalAndNature(e.letter, e.animal, e.natureFact, language);
    }
  };
  const n = Math.min(concurrency, entries.length);
  await Promise.all(Array.from({ length: n }, () => worker()));
}

/**
 * Stop any current speech (Web Speech API or audio playback) and
 * invalidate any in-flight `speak()` whose fetch hasn't resolved yet.
 */
export function stop(): void {
  speechSynthesis.cancel();
  if (currentAudio) {
    currentAudio.pause();
    currentAudio.currentTime = 0;
    currentAudio = null;
  }
  speakRequestId++;
  emitSpeech({ state: 'idle', text: null, language: null });
}
